import { IStatementLayouts } from './statement-layout.model';

/**
 * The wire shape of a closing's account surface.
 *
 * Mirrors what `aumico-be` answers on `GET /api/projects/:projectId/accounts` rather than importing
 * its types: the backend package is not a dependency of this library, and a mirrored interface that
 * drifts fails at the one place it is mapped instead of everywhere it is used.
 *
 * AMOUNTS ARE MINOR UNITS. The backend aggregates in Rappen, so every figure below is 100× the
 * displayed value. The mapping to the table divides once, in one place.
 */

/** Whether the account still exists in the ERP. An account is never deleted, only marked. */
export type AccountPresence = 'IN_ERP' | 'REMOVED_FROM_ERP' | 'JOURNAL_ONLY';

/**
 * How far the assignment to a position has got. NOT the document reconciliation.
 *
 * `AI_UNRESOLVED` replaced `UNMAPPED` on 24.08.2026, and the rename carries the whole point: with
 * `UNMAPPED` a reader could not tell whether the classifier had already been there. It had, and it
 * gave up — so this is the list that belongs in front of a trustee, and nothing but a human decision
 * will move it. `PENDING` is the opposite: the machine is still working, and showing those accounts
 * as a to-do would ask a person to do work that is about to be done for them.
 */
export type AccountClassificationState = 'PENDING' | 'CLASSIFIED' | 'AI_UNRESOLVED';

/** Where an assignment came from. */
export type AccountBindingSource = 'IMPORT' | 'MANUAL' | 'CLASSIFIER' | 'INHERITED';

/**
 * Whether the documents match the balance — the axis the status chips filter on. The mapping onto the
 * three chips is in the page, not here.
 */
export type ReconciliationState = 'NOT_ATTEMPTED' | 'MATCHED' | 'DIFFERENCE' | 'MACHINE_DISCARDED';

/** Why the account shows its chip — the kernel's `AccountReasonCode`. */
export type AccountReasonCode =
    | 'DOCUMENT_MISSING'
    | 'DOCUMENT_BEING_READ'
    | 'DOCUMENT_UNREADABLE'
    | 'DOCUMENT_DATE_MISMATCH'
    | 'PROOFS_CONTRADICT'
    | 'TRANSIT_NOT_BALANCED'
    | 'RECONCILED_WITH_DOCUMENT';

/**
 * The line under the chip on the account card, as the backend stores it: a code and the facts the
 * sentence names, never prose. Amounts in minor units.
 */
export interface IAccountReason {
    code: AccountReasonCode;
    /** A kernel `DocumentKind` identifier. */
    documentKind: string | null;
    /** ISO `YYYY-MM-DD` — the proof's balance date. */
    documentDate: string | null;
    amount: IAccountMoney | null;
    /** `PROOFS_CONTRADICT` only: every proof and what it says. */
    conflicting: { kind: string; amount: IAccountMoney }[];
    /** Securities only: which of the two read values matched. */
    basis: 'MARKET_VALUE' | 'COST' | null;
}

/**
 * Where a document value came from — REV-22005 (`entscheidregister.md` N-3.4). DERIVED by the backend
 * on read (a value with no run id beside it is the trustee's); there is no stored origin field.
 */
export type DocumentBalanceOrigin = 'MACHINE' | 'HUMAN';

/** ISO bounds of one period. The only way a period is ever named — never a year. */
export interface IPeriodBounds {
    /** ISO `YYYY-MM-DD`, first day. */
    periodStart: string;
    /** ISO `YYYY-MM-DD`, last day, inclusive. */
    periodEnd: string;
}

/** An amount in minor units, with the currency it is denominated in. */
export interface IAccountMoney {
    amount: number;
    currency: string;
}

/** One row as the backend sends it. */
export interface IProjectAccount {
    accountId: string;
    accountNumber: string;
    name: string;
    presence: AccountPresence;

    /** The position, or null when the account is not assigned to one. */
    accountGroup: string | null;
    accountFunction: string | null;

    /**
     * The two halves are assigned SEPARATELY and fail separately, so each carries its own provenance
     * and its own state. One pair for the position, one for the function.
     *
     * That matters for display: a group set by a human and a function set by the model can sit on the
     * same account, so anything that marks "assigned by the AI" has to mark it per half.
     */
    groupSource: AccountBindingSource | null;
    functionSource: AccountBindingSource | null;
    groupClassification: AccountClassificationState;
    /**
     * `null` when the position does not carry a function at all — the normal case, not a missing
     * value. Only the two positions with a function catalogue ever hold something here.
     */
    functionClassification: AccountClassificationState | null;

    /** Null for an account posted in several currencies — normal, not an error. */
    saldo: IAccountMoney | null;
    saldoInBaseCurrency: IAccountMoney;
    /**
     * One entry per comparison period the RESPONSE reports, positionally — same length, same order.
     *
     * `null` where that period's status is `NO_DATA`. Not a zero: a zero would say the period exists
     * and carried no posting on this account, which is a different statement.
     */
    comparisons: (IAccountComparison | null)[];
    /** Null means there is NO prior year. A prior year without a posting is zero. */
    priorYearSaldo: IAccountMoney | null;
    delta: IAccountMoney | null;
    /**
     * The backend also sends this, and the table does NOT use it: the change is computed locally from
     * the two balances, so a formatting decision stays a display decision.
     */
    deltaPercent: number | null;

    reconciliation: ReconciliationState;
    evidenceComplete: boolean;
    flag: { reason: string; at: string } | null;
    /**
     * The evidence-side balance from the last reconciliation measurement — from #160.
     * `null`/absent = never measured, or the evidence side was not read. The difference to the ERP
     * balance is computed here, not sent (`documentDifferenceOf`).
     *
     * ⚠️ `amountMinor`, NOT `amount` — unlike {@link IAccountMoney}. Spreading over the wrong field
     * name yields `undefined`, which the account detail page renders as an empty value rather than
     * throwing, so the naming difference is deliberate and must survive any refactor that tries to
     * fold this into `IAccountMoney`.
     */
    documentBalance?: { amountMinor: number; currency: string } | null;
    /**
     * REV-22005 — `'HUMAN'` when a trustee entered {@link documentBalance} by hand, `'MACHINE'` when a
     * run read it, `null`/absent without a value.
     */
    documentBalanceOrigin?: DocumentBalanceOrigin | null;
    /** The line under the chip on the account card; `null`/absent = nothing to say. */
    reason?: IAccountReason | null;
    /** REV-22302 — what the AI does with the account's documents (grey card on the account detail). */
    aiReading?: IAccountAiReading | null;
}

export interface IAccountAiReading {
    /** RECONCILING: a value was read and its closing run has not answered yet. */
    state: 'READING' | 'RECONCILING' | 'VALUE' | 'REFUSED' | 'OPEN' | 'MANUAL';
    documentId?: string;
    documentName?: string;
    amountMinor?: number;
    currency?: string;
    asOfDate?: string;
    quote?: string;
    line?: string;
    reason?: string;
    /** The document kind a run's reason is about, e.g. `BANK_STATEMENT`. */
    kind?: string;
    /** READING: the step the document is in — file check, text recognition, or the reading itself. */
    step?: string;
    manualAmountMinor?: number;
}

/**
 * REV-22005 — what a human account-state write answers: the fields of ONE row it can move.
 *
 * The reducer patches the matching row with it instead of reloading the list, which would decrypt a
 * journal per comparison period for a change the server already described.
 */
export interface IAccountStatePatch {
    accountId: string;
    reconciliation: ReconciliationState;
    evidenceComplete: boolean;
    documentBalance: { amountMinor: number; currency: string } | null;
    documentBalanceOrigin: DocumentBalanceOrigin | null;
    flag: { reason: string; at: string } | null;
}

/**
 * REV-22005 — the three things a trustee does to one account's state. A discriminated union rather
 * than three action pairs: one write at a time per account, one status, one error — the dialog that
 * is open is the one the error belongs to.
 */
export type AccountStateCommand =
    | { kind: 'reviewFlag'; reason: string }
    | { kind: 'reviewUnflag' }
    | {
          kind: 'documentBalance';
          /** Integer minor units in the LEDGER sign — the host converts from what it displays. */
          amountMinor: number;
          currency: string;
          note: string | null;
      }
    | { kind: 'documentBalanceClear' };

/** One account's figures for one comparison period. Minor units, like everything else here. */
export interface IAccountComparison {
    saldo: IAccountMoney;
    delta: IAccountMoney;
    /** Sent and NOT used — the change is computed locally, same reasoning as `deltaPercent` above. */
    deltaPercent: number | null;
}

/** What the backend found for one requested comparison period. */
export interface IComparisonPeriod extends IPeriodBounds {
    /** `AVAILABLE` = a journal was read. `NO_DATA` = asked for, and the backend holds none. */
    status: 'AVAILABLE' | 'NO_DATA';
}

/**
 * The account list as the route answers it.
 *
 * An object and not an array, because the rows alone cannot say which period a column shows. Columns
 * are labelled from `comparisons` and from `period` — never from a date this library computes, which
 * is how a column ends up titled with a period the backend did not read.
 */
export interface IProjectAccountsResponse {
    /** The closing's own period, or null when no journal was found for it. */
    period: IPeriodBounds | null;
    comparisons: IComparisonPeriod[];
    accounts: IProjectAccount[];
    /**
     * How many accounts the backend left out for being zero in every period answered.
     *
     * Part of the wire shape and deliberately NOT read: the «Nullkonten» chip is what brings them
     * back, and a banner counting them alongside it was noise. Mirrored here because this interface
     * describes what the route answers, not what this screen happens to use.
     */
    hiddenZeroAccounts: number;
    /**
     * How the two statements are structured. Absent from a backend older than the layout, and the
     * screens then fall back to a flat list of positions.
     */
    statementLayouts?: IStatementLayouts;
    /**
     * The closing's currency from its settings, for the statement headings. Never an account's.
     * Null while the closing has none; absent from a backend older than it.
     */
    currency?: string | null;
}

/**
 * One period the backend HOLDS a journal for.
 *
 * What we have, not what the ERP has — Abacus cannot enumerate its fiscal years, so no route claims
 * to know which ones exist. This is the list the comparison chips are built from.
 */
export interface IJournalPeriod extends IPeriodBounds {
    syncedAt: string;
}

/**
 * A closing's sync state: when it last pulled, and which periods it holds.
 *
 * The two are NOT interchangeable. A period document is per company and shared by every closing of
 * it, so `syncedAt` above says when anyone last synced the company; `lastErpSyncAt` says when THIS
 * closing did. The header asks the second question.
 */
export interface IClosingSyncState {
    /** Null when this closing has never been pulled. */
    lastErpSyncAt: string | null;
    periods: IJournalPeriod[];

    /**
     * The period this closing COVERS — from the closing itself, not from what we hold.
     *
     * Not the same as the account list's `period`, and the sync dialog needs this one. That field
     * reports the period a journal was actually READ for, so a column of figures is never labelled
     * with a year we hold no data for — which makes it null on a closing that never synced. The
     * dialog proposes comparison periods by stepping back from the closing's own period, and before
     * the first pull there is nothing held to step back from.
     *
     * Null when the closing stores neither a period nor a balance-sheet date to derive one from.
     */
    closingPeriod: IPeriodBounds | null;
}

/** The polled answer. Counters only — this route never touches the journal. */
/**
 * The counters the client polls. One aggregation, no amounts, no journal, no decryption.
 *
 * Counted per HALF, not per account: an account can be waiting on its function while its group is
 * long settled, and one number over both would hide exactly that.
 */
export interface IClassificationSummary {
    total: number;
    groupClassified: number;
    groupPending: number;
    groupAiUnresolved: number;
    functionPending: number;
    functionAiUnresolved: number;
    /**
     * The oldest outstanding request, or `null` when nothing waits.
     *
     * LOAD-BEARING for the surface. A classification request can be lost in transit and there is no
     * scheduler that notices — the READER computes staleness from this. Without that check the banner
     * says "the AI is working" forever, and nobody can tell that apart from a request that is gone.
     */
    oldestRequestedAt: string | null;
}

/**
 * One account's state on the reconciliation counter route (#160) — never an amount.
 *
 * Mirrors the be contract in `REV-21869-context/sprint3/160/contract.md` («Runde 3»), the same three
 * keys the account list carries for these two fields.
 */
export interface IReconciliationStatusAccount {
    accountId: string;
    reconciliation: ReconciliationState;
    evidenceComplete: boolean;
}

/**
 * The counters the reconciliation poll reads — `GET …/accounts/reconciliation-status` (#160).
 *
 * Counters plus `total`, and one row per account. No amounts, no journal: this route exists so the
 * poll (#162) never has to touch `getAccounts()`, which decrypts a journal per comparison period.
 */
export interface IReconciliationStatusResponse {
    total: number;
    notAttempted: number;
    matched: number;
    withDifference: number;
    accounts: IReconciliationStatusAccount[];
    /** REV-22005 — accounts whose machine reconciliation a trustee discarded (N-2.2). Optional: older backends omit it. */
    machineDiscarded?: number;
}

/** What one manual assignment sets. A null position means "not classifiable", which is an answer. */
export interface IAccountBindingWrite {
    accountId: string;
    /** Absent = this write does not touch the position (a function-only write, REV-22479). */
    accountGroup?: string | null;
    accountFunction?: string | null;
}

/** What the write route answers: counters, never accounts. */
export interface IAccountBindingWriteResult {
    requested: number;
    /** Lower than requested when an account has no state document in this closing. Not an error. */
    applied: number;
}

/** The body of the closing's ERP sync. */
export interface IAbcpSyncBody {
    vendor: string;
    connectionId?: string;
    externalCompanyId?: string;
    /**
     * Comparison periods to FILL IN. At most four.
     *
     * Only periods the backend holds no journal for are pulled; one already stored is never
     * overwritten, because it may belong to a closing that is finished.
     */
    comparisonPeriods?: IPeriodBounds[];
}

/** What the sync did with one comparison period it was asked to fill. */
export interface IComparisonPeriodOutcome extends IPeriodBounds {
    outcome: 'FILLED' | 'ALREADY_PRESENT' | 'FAILED';
}

/** What the sync answers — its own tally, not a job id. */
export interface IAbcpSyncResult {
    projectId: string;
    companyId: string;
    vendor: string;
    periodStart: string;
    periodEnd: string;
    accountsInErp: number;
    accountsJournalOnly: number;
    accountsRemovedFromErp: number;
    statesCreated: number;
    statesReset: number;
    /** Per comparison period asked for. `FAILED` is the one worth showing the user. */
    comparisonPeriods: IComparisonPeriodOutcome[];
    /** Documents of the closing's period the ERP would not hand over: their postings show no VAT code. */
    vatDocumentsUnreadable: number;
}
